import {getQualityAgentToken} from '~api/auth';
import {AuthService} from '~api/auth.service';

import {EmbedContext} from './protocol';
import {jwtExpiresAt} from './sessionToken';

// Registry of embedded sub-apps the Maddox host can mount. Adding a new app is
// a matter of an entry here plus a token audience registered on the API
// (Backend `application/token/embed_token.py`).

export interface EmbeddedAppConfig {
  // Base URL of the sub-app; the iframe src is derived from this.
  url: string;
  // Origin allowed to exchange postMessages with us (derived from `url`).
  origin: string;
  // JWT audience the minted token must carry (matches the API registry).
  tokenAudience: string;
  // Fetches a fresh short-lived token for this app from the Maddox API.
  fetchToken: () => Promise<{token: string; expiresAt: string}>;
  buildSrc?: (baseUrl: string, context: EmbedContext) => string;
  // Height of the bottom bar the sub-app draws itself. Host chrome floating
  // above the iframe (the Ask AI button) cannot measure across the iframe
  // boundary, so it reads this to stay clear of it.
  bottomChromeHeight?: number;
  // Adds `allow-modals` to this app's iframe sandbox, for a sub-app that
  // reports errors with alert()/confirm() — without it the browser drops the
  // call silently. Opt-in per app: blocking dialogs over the host are not a
  // capability every embedded app should get.
  allowModals?: boolean;
}

const QUALITY_AGENT_URL = import.meta.env.VITE_QUALITY_AGENT_URL ?? '';
const MEASURING_APP_URL = import.meta.env.VITE_MEASURING_APP_URL ?? '';
const DEFECT_STUDIO_URL = import.meta.env.VITE_DEFECT_STUDIO_URL ?? '';
const MEASURING_APP_FOOTER_HEIGHT = 56;

// The origin of a configured app URL, or '' when the value is not an absolute
// http(s) URL. Evaluated while this registry is imported, so a typo in one
// app's env var must not throw — the app then just reads as unconfigured
// (see `isEmbeddedAppConfigured`) instead of taking the whole host down.
function originOf(url: string): string {
  if (!url) return '';
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.origin : '';
  } catch {
    return '';
  }
}

export function hostEnvironmentId(): 'dev' | 'staging' | 'prod' {
  const authApi = import.meta.env.VITE_AUTH_API ?? '';
  if (authApi.includes('staging-api')) return 'staging';
  if (authApi.includes('dev-api') || authApi.includes('localhost') || authApi === '') return 'dev';
  return 'prod';
}

// Hands the host's own Maddox session token to a sub-app registered with the
// `maddox-session` audience, so it can call the Maddox API as the signed-in user
// (no separate token is minted for it). Shared by every such app; the name only
// makes the failure message point at the app that asked.
async function fetchMaddoxSessionToken(appName: string): Promise<{token: string; expiresAt: string}> {
  const token = AuthService.getToken();
  if (!token) throw new Error(`No Maddox session token to hand to the ${appName}`);
  const expiresAt = jwtExpiresAt(token);
  if (!expiresAt) throw new Error('Maddox session token carries no expiry');
  return {token, expiresAt};
}

function buildMeasuringAppSrc(baseUrl: string, context: EmbedContext): string {
  const url = new URL(baseUrl);
  const basePath = url.pathname.replace(/\/+$/, '');
  const segments = [context.customer, context.projectId, context.modelId]
    .filter((segment): segment is string => Boolean(segment))
    .map(encodeURIComponent);
  url.pathname = `${basePath}/${segments.join('/')}`;
  url.searchParams.set('embedded', '1');
  url.searchParams.set('env', hostEnvironmentId());
  if (context.language) url.searchParams.set('lang', context.language);
  return url.toString();
}

// The studio is served under a path of its own (`/db/`), so the configured URL's
// path is kept as-is and only query parameters are added. Everything here is
// non-secret: the session token is handed over later, over postMessage.
function buildDefectStudioSrc(baseUrl: string, context: EmbedContext): string {
  const url = new URL(baseUrl);
  url.searchParams.set('embedded', '1');
  url.searchParams.set('customer', context.customer);
  url.searchParams.set('projectID', context.projectId);
  // The model the studio opens with — it is embedded from a model's samples page.
  if (context.modelId) url.searchParams.set('modelID', context.modelId);
  // The defect class every accepted variant is filed under (chosen before the studio opens).
  if (context.labelId) url.searchParams.set('labelID', context.labelId);
  if (context.labelId && context.labelName) url.searchParams.set('labelName', context.labelName);
  url.searchParams.set('env', hostEnvironmentId());
  if (context.language) url.searchParams.set('lang', context.language);
  return url.toString();
}

export function isEmbeddedAppConfigured(appId: string): boolean {
  const app = EMBEDDED_APPS[appId];
  return Boolean(app?.url && app.origin);
}

export const EMBEDDED_APPS: Record<string, EmbeddedAppConfig> = {
  'quality-agent': {
    url: QUALITY_AGENT_URL,
    origin: originOf(QUALITY_AGENT_URL),
    tokenAudience: 'quality-agent',
    fetchToken: async () => {
      const {token, expiresAt} = await getQualityAgentToken();
      return {token, expiresAt};
    },
  },
  'measuring-app': {
    url: MEASURING_APP_URL,
    origin: originOf(MEASURING_APP_URL),
    tokenAudience: 'maddox-session',
    fetchToken: () => fetchMaddoxSessionToken('measuring app'),
    buildSrc: buildMeasuringAppSrc,
    bottomChromeHeight: MEASURING_APP_FOOTER_HEIGHT,
  },
  'defect-studio': {
    url: DEFECT_STUDIO_URL,
    origin: originOf(DEFECT_STUDIO_URL),
    tokenAudience: 'maddox-session',
    fetchToken: () => fetchMaddoxSessionToken('defect studio'),
    buildSrc: buildDefectStudioSrc,
    // The studio reports every failure with alert()/confirm().
    allowModals: true,
  },
};
